import React, { useState, useRef, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';

interface UserAvatarProps {
  size?: 'small' | 'medium' | 'large';
  showDropdown?: boolean;
  className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ 
  size = 'medium', 
  showDropdown = true,
  className = '' 
}) => {
  const { userProfile, profilePhotoUrl, signOut } = useUser();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleAvatarClick = () => {
    if (showDropdown) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleSignOut = async () => {
    setIsDropdownOpen(false);
    await signOut();
  };

  // Size configurations
  const sizeConfig = {
    small: { width: '32px', height: '32px', fontSize: '0.75rem' },
    medium: { width: '40px', height: '40px', fontSize: '0.875rem' },
    large: { width: '48px', height: '48px', fontSize: '1rem' }
  };

  const config = sizeConfig[size];

  // Get initials if no profile photo
  const getInitials = () => {
    if (!userProfile) return 'U';
    const names = userProfile.displayName?.split(' ') || ['User'];
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const avatarStyle: React.CSSProperties = {
    width: config.width,
    height: config.height,
    borderRadius: '50%',
    cursor: showDropdown ? 'pointer' : 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: config.fontSize,
    fontWeight: '600',
    color: 'white',
    position: 'relative',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    ...(profilePhotoUrl 
      ? {
          backgroundImage: `url(${profilePhotoUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }
      : {
          backgroundColor: '#6366f1',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
        }
    )
  };

  const hoverStyle = showDropdown ? {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
  } : {};

  return (
    <div className={`user-avatar-container ${className}`} style={{ position: 'relative' }} ref={dropdownRef}>
      <div
        className="user-avatar"
        style={avatarStyle}
        onClick={handleAvatarClick}
        onMouseEnter={(e) => {
          if (showDropdown) {
            Object.assign(e.currentTarget.style, hoverStyle);
          }
        }}
        onMouseLeave={(e) => {
          if (showDropdown) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
        title={userProfile?.displayName || 'User Avatar'}
      >
        {!profilePhotoUrl && getInitials()}
      </div>

      {showDropdown && isDropdownOpen && (
        <div
          className="avatar-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '0.5rem',
            backgroundColor: 'var(--dark-secondary, #1a1a2e)',
            border: '1px solid var(--border-color, #2a2a3e)',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            minWidth: '200px',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          {/* User Info */}
          <div
            style={{
              padding: '1rem',
              borderBottom: '1px solid var(--border-color, #2a2a3e)'
            }}
          >
            <div style={{ color: 'var(--text-primary, #ffffff)', fontWeight: '600', fontSize: '0.875rem' }}>
              {userProfile?.displayName || 'User'}
            </div>
            <div style={{ color: 'var(--text-secondary, #a1a1aa)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
              {userProfile?.mail || userProfile?.userPrincipalName || 'user@example.com'}
            </div>
            {userProfile?.jobTitle && (
              <div style={{ color: 'var(--text-secondary, #a1a1aa)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                {userProfile.jobTitle}
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div style={{ padding: '0.5rem 0' }}>
            <button
              onClick={handleSignOut}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'var(--text-primary, #ffffff)',
                fontSize: '0.875rem',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--dark-tertiary, #2a2a3e)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span>🚪</span>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
